import React, { useRef, useState } from 'react';
import WorkTypeCard, { BLANK_ROW, fillColors, fromSample, nextColor } from './WorkTypeCard';
import './PayrollSettings.css';

// 한 달치 대장 설정(계약서 수당·근무타입) 편집기. 여기서 바꾼 내용은 그 달에만 적용된다.
// section 을 주면('allowances' | 'worktypes') 그 부분만 보인다 — 대장 화면의 수당 설정 탭·근무타입 설정 탭.
// 두 탭은 같은 편집기를 이어 쓰므로, 탭을 오가도 저장하지 않은 내용이 남아 있고 저장은 한꺼번에 된다.
// 수당은 시수연동·고정수당을 한 표에 모아 열 위치를 맞춘다(구분이 같은 자리에 온다).
// 계약외 수당과 기타 공제항목은 임금대장 화면에서 열을 직접 추가·삭제한다.

const KINDS = ['기본', '통상', '비통상'];
const MULTIPLIERS = [0.5, 1, 1.5, 2];
const SECTIONS = [
  { list: 'wage_items', tag: '시수연동', cls: 'wage', blank: { name: '', multiplier: 1, kind: '기본' } },
  { list: 'fixed_items', tag: '고정수당', cls: 'fixed', blank: { name: '', kind: '통상', tax_free: false, tax_free_type: '', tax_free_limit: null } },
];

const move = (list, i, d) => {
  const j = i + d;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

// 선택된 값의 색. 가장 흔한 기본값(×1·기본·과세)은 옅은 회색으로 두고 나머지만 색을 줘서,
// 표를 훑을 때 기본과 다른 줄이 눈에 들어오게 한다.
const MULTIPLIER_TONES = { 0.5: 'sky', 1: 'plain', 1.5: 'amber', 2: 'red' };
const KIND_TONES = { 기본: 'plain', 통상: 'blue', 비통상: 'orange' };
const TAX_TONES = { false: 'plain', true: 'green' };

// 몇 개 안 되는 선택지는 드롭다운 대신 한 번에 누르는 토글로
function Toggle({ options, value, onChange, labels, tones = {} }) {
  return (
    <div className="pps-toggle">
      {options.map((opt, i) => (
        <button
          type="button"
          key={String(opt)}
          className={opt === value ? `on tone-${tones[opt] || 'violet'}` : ''}
          onClick={() => onChange(opt)}
        >
          {labels ? labels[i] : opt}
        </button>
      ))}
    </div>
  );
}

function PayrollSettings({ title, initial, taxFreeItems = [], workTypeSamples = [], submitLabel, onSubmit, onClose, onDownloadForm, onUploadForm, inline = false, section = null }) {
  // 근무타입마다 열 때의 이름(was)을 달아 둔다. 이름을 바꿔 저장하면 서버가 그 타입을 쓰던 직원의 근무타입도 함께 바꾼다
  const [settings, setSettings] = useState(() => ({
    ...initial,
    work_types: fillColors(initial.work_types || []).map((wt) => ({ ...wt, was: wt.type })),
  }));
  const [saved, setSaved] = useState(settings);      // 마지막으로 저장한 상태(저장하지 않은 변경이 있는지 본다)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const formFile = useRef(null);
  const [loaded, setLoaded] = useState('');
  const [dropping, setDropping] = useState(false);

  // 설정 양식(엑셀)을 읽어 이 화면에 채운다. 저장은 하지 않는다 — 확인하고 저장을 눌러야 반영된다.
  const loadForm = (file) => runForm(async () => {
    const parsed = await onUploadForm(file);
    // 이름이 같은 수당은 id 를 이어받는다 → 이미 입력된 직원 값이 그대로 붙는다
    const keepIds = (list, before) => list.map((item) => {
      const same = before.find((x) => x.name === item.name);
      return same && same.id ? { ...item, id: same.id } : item;
    });
    setSettings((prev) => ({
      ...prev,
      wage_items: keepIds(parsed.wage_items, prev.wage_items),
      fixed_items: keepIds(parsed.fixed_items, prev.fixed_items),
      // 양식에는 색이 없다 → 이름이 같은 타입의 색을 이어받고, 새 타입에는 안 쓴 색을 준다
      work_types: fillColors(parsed.work_types.map((wt) => ({ ...wt, color: (prev.work_types.find((x) => x.type === wt.type) || {}).color }))),
    }));
    setLoaded(`양식을 불러왔습니다: 시수연동 ${parsed.wage_items.length}개, 고정수당 ${parsed.fixed_items.length}개, 근무타입 ${parsed.work_types.length}개. 확인하고 저장하세요.`);
  });

  // 파일을 끌어다 놓으면 설정 양식으로 읽는다
  const hasFiles = (e) => Array.from(e.dataTransfer.types || []).includes('Files');
  const dropHandlers = onUploadForm ? {
    onDragOver: (e) => { if (hasFiles(e)) { e.preventDefault(); setDropping(true); } },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false); },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDropping(false);
      const file = e.dataTransfer.files[0];
      if (!file) return;
      if (!/\.xlsx$/i.test(file.name)) { setError('엑셀(.xlsx) 설정 양식만 올릴 수 있습니다'); return; }
      loadForm(file);
    },
  } : {};

  // 설정 양식(계약서 수당 + 근무타입) 내려받기·올리기
  const runForm = async (fn) => {
    setSaving(true);
    setError('');
    setLoaded('');
    try {
      await fn();
    } catch (e) {
      setError(e.message || '실패했습니다');
    } finally {
      setSaving(false);
      if (formFile.current) formFile.current.value = '';
    }
  };

  const setList = (name, list) => setSettings((s) => ({ ...s, [name]: list }));
  const patch = (name, i, changes) =>
    setList(name, settings[name].map((item, k) => (k === i ? { ...item, ...changes } : item)));
  const remove = (name, i) => setList(name, settings[name].filter((_, k) => k !== i));

  // 비과세 유형 하나는 수당 하나에만 쓴다 → 이미 쓴 유형은 목록에서 뺀다
  const usedTypes = new Set(
    [...settings.fixed_items, ...settings.extra_items].filter((i) => i.tax_free && i.tax_free_type).map((i) => i.tax_free_type));
  const freeTypes = taxFreeItems.filter((t) => !usedTypes.has(t.name));
  const limitText = (name) => {
    const t = taxFreeItems.find((x) => x.name === name);
    if (!t) return '';
    return t.monthly_limit != null ? `${t.monthly_limit.toLocaleString('ko-KR')}원` : '이 달은 제도 없음';
  };

  const rowButtons = (name, i, locked, firstMovable = 0) => (
    <td className="pps-actions">
      <button type="button" className="pps-icon" onClick={() => setList(name, move(settings[name], i, -1))} disabled={locked || i <= firstMovable} title="위로">▲</button>
      <button type="button" className="pps-icon" onClick={() => setList(name, move(settings[name], i, 1))} disabled={locked || i === settings[name].length - 1} title="아래로">▼</button>
      <button type="button" className="pps-icon danger" onClick={() => remove(name, i)} disabled={locked} title="제거">✕</button>
    </td>
  );

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await onSubmit(settings);
      // 저장한 이름이 이제 '바꾸기 전 이름'이다(창을 닫지 않고 또 바꿔도 직원의 근무타입이 따라가게)
      const next = { ...settings, work_types: settings.work_types.map((wt) => ({ ...wt, was: String(wt.type || '').trim() })) };
      setSettings(next);
      setSaved(next);
      setSaving(false);
    } catch (e) {
      setError(e.message || '저장에 실패했습니다');
      setSaving(false);
    }
  };

  const addTaxFreeAllowance = (type) => {
    setList('fixed_items', [...settings.fixed_items, {
      name: type.name, kind: type.name === '식대' ? '통상' : '비통상', tax_free: true, tax_free_type: type.name, tax_free_limit: null,
    }]);
  };

  const addSample = (name) => {
    const sample = workTypeSamples.find((s) => s.type === name);
    if (sample) setList('work_types', [...settings.work_types, fromSample(sample, settings.work_types)]);
  };

  const allowanceRow = (sec, item, i) => {
    const { list, tag, cls } = sec;
    const isWage = list === 'wage_items';
    const isBase = isWage && i === 0;
    const multiplier = Number(item.multiplier);
    // 네 가지 밖의 배수가 이미 들어 있으면 그 값도 토글에 보여 준다
    const multipliers = isWage && !MULTIPLIERS.includes(multiplier) ? [...MULTIPLIERS, multiplier] : MULTIPLIERS;
    return (
      <tr key={`${list}-${item.id || `new-${i}`}`} className={i === 0 ? 'pps-first' : ''}>
        <td><span className={`pps-tag ${cls}`}>{tag}</span></td>
        <td><input value={item.name} placeholder="수당명" onChange={(e) => patch(list, i, { name: e.target.value })} /></td>
        <td>
          {isWage && (
            <Toggle options={multipliers} labels={multipliers.map((m) => `×${m}`)} tones={MULTIPLIER_TONES} value={multiplier} onChange={(m) => patch(list, i, { multiplier: m })} />
          )}
        </td>
        <td>
          <Toggle options={KINDS} tones={KIND_TONES} value={item.kind} onChange={(k) => patch(list, i, { kind: k })} />
        </td>
        <td>
          {!isWage && (
            <Toggle
              options={[false, true]}
              labels={['과세', '비과세']}
              tones={TAX_TONES}
              value={!!item.tax_free}
              onChange={(v) => patch(list, i, v ? { tax_free: true } : { tax_free: false, tax_free_type: '', tax_free_limit: null })}
            />
          )}
        </td>
        <td>
          {!isWage && item.tax_free && (
            <select
              className={item.tax_free_type ? '' : 'pps-required'}
              value={item.tax_free_type || ''}
              onChange={(e) => patch(list, i, { tax_free_type: e.target.value, tax_free_limit: null })}
            >
              <option value="">유형 선택…</option>
              {/* 지금 고른 유형 + 아직 아무 수당도 안 쓴 유형 */}
              {taxFreeItems.filter((t) => t.name === item.tax_free_type || !usedTypes.has(t.name)).map((t) => (
                <option key={t.name} value={t.name}>{t.name}</option>
              ))}
            </select>
          )}
        </td>
        <td className="pps-limit">{!isWage && item.tax_free ? limitText(item.tax_free_type) : ''}</td>
        {rowButtons(list, i, isBase, isWage ? 1 : 0)}
      </tr>
    );
  };

  return (
    <div className={inline ? 'pps-inline' : 'pps-overlay'}>
      <div className={`pps-modal ${dropping ? 'dropping' : ''}`} {...dropHandlers}>
        {dropping && <div className="pps-drop">여기에 놓으면 설정 양식을 불러옵니다<span>계약서 수당과 근무타입이 양식 내용으로 바뀝니다 · 저장을 눌러야 반영됩니다</span></div>}
        <div className="pps-header">
          <h3>{title}</h3>
          {onUploadForm && <span className="pps-muted">설정 양식(.xlsx)을 이 화면에 끌어다 놓아도 됩니다</span>}
          {!inline && <button type="button" className="pps-icon" onClick={onClose} title="닫기">✕</button>}
        </div>

        <div className="pps-body">
          {section !== 'worktypes' && (
          <section className="pps-sec-allowances">
            <h4>계약서 수당</h4>
            <table className="pps-table pps-allowances">
              <thead>
                <tr>
                  <th style={{ width: 76 }}>종류</th>
                  <th>수당명</th>
                  <th style={{ width: 176 }}>배수</th>
                  <th style={{ width: 168 }}>구분</th>
                  <th style={{ width: 116 }}>과세</th>
                  <th style={{ width: 130 }}>비과세 유형</th>
                  <th style={{ width: 110 }}>월 한도</th>
                  <th style={{ width: 92 }} />
                </tr>
              </thead>
              <tbody>
                {SECTIONS.map((sec) => settings[sec.list].map((item, i) => allowanceRow(sec, item, i)))}
              </tbody>
            </table>
            <div className="pps-add-row">
              {SECTIONS.map((sec) => (
                <button type="button" key={sec.list} className="btn btn-secondary btn-sm" onClick={() => setList(sec.list, [...settings[sec.list], { ...sec.blank }])}>
                  + {sec.tag} 수당
                </button>
              ))}
              {freeTypes.length > 0 && (
                <span className="pps-quick">
                  <span className="pps-muted">비과세 수당 바로 추가</span>
                  {freeTypes.map((t) => (
                    <button type="button" key={t.name} className="pps-chip" onClick={() => addTaxFreeAllowance(t)} title="고정수당으로 추가합니다">
                      + {t.name}{t.monthly_limit != null ? ` (월 ${t.monthly_limit.toLocaleString('ko-KR')})` : ''}
                    </button>
                  ))}
                </span>
              )}
            </div>
            <p className="pps-note">
              <b>시수연동</b>: 계약서에 미가산 시수를 넣으면 통상시급 × 시수 × 배수로 계산합니다. 첫 줄은 기본급입니다(제거·이동 불가).
              {' '}<b>고정수당</b>: 계약서에 금액을 직접 넣는, 시간과 무관한 수당.
              {' '}비과세 한도는 관리자 메뉴 › 급여 기준값의 유형별 값을 씁니다.
              {' '}계약서에 없는 <b>계약외 수당</b>과 <b>기타 공제항목</b>은 임금대장 화면의 「+ 계약외 수당」 「+ 공제항목」으로 열을 추가하고, 열 머리의 ✕ 로 뺍니다.
            </p>
          </section>
          )}

          {section !== 'allowances' && (
          <section className="pps-sec-worktypes">
            <h4>근무타입 <span className="pps-muted">계약서 근무시간표 · 색은 임금대장의 근무타입 칸에 칠해집니다</span></h4>
            <div className="pps-worktype-add">
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setList('work_types', [...settings.work_types, { type: '', color: nextColor(settings.work_types), rows: [{ ...BLANK_ROW }] }])}>+ 근무타입 추가</button>
              {workTypeSamples.length > 0 && (
                <select value="" onChange={(e) => addSample(e.target.value)}>
                  <option value="">샘플에서 추가…</option>
                  {workTypeSamples.map((s) => <option key={s.type} value={s.type}>{s.type} ({s.rows.map((r) => r.days).join(' / ')})</option>)}
                </select>
              )}
            </div>
            <div className="pps-worktypes">
              {settings.work_types.map((wt, i) => (
                <WorkTypeCard
                  key={`wt-${i}`}
                  wt={wt}
                  taken={new Set(settings.work_types.filter((_, k) => k !== i).map((w) => w.color))}
                  onChange={(changes) => patch('work_types', i, changes)}
                  onRemove={() => remove('work_types', i)}
                />
              ))}
            </div>
          </section>
          )}
        </div>

        <div className="pps-footer">
          {onDownloadForm && (
            <div className="pps-form-tools">
              <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => runForm(() => onDownloadForm(settings))} title="지금 창에 보이는 계약서 수당과 근무타입을 엑셀로 내려받습니다(저장 전 상태 그대로).">
                ⬇ 설정 양식 내려받기
              </button>
              <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => formFile.current && formFile.current.click()} title="고친 설정 양식을 올리면 이 창의 계약서 수당과 근무타입이 양식 내용으로 바뀝니다. 저장을 눌러야 반영됩니다.">
                ⬆ 설정 양식 올리기
              </button>
              <input
                ref={formFile}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files[0] && loadForm(e.target.files[0])}
              />
            </div>
          )}
          {!error && loaded && <span className="pps-loaded">{loaded}</span>}
          {!error && !loaded && inline && settings !== saved && <span className="pps-dirty">저장하지 않은 변경이 있습니다{section ? ' (수당·근무타입 탭을 함께 저장합니다)' : ''}</span>}
          {error && <span className="pps-error">{error}</span>}
          {!inline && <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>취소</button>}
          <button type="button" className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중…' : submitLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default PayrollSettings;
